import { Form, Formik, FormikProps, FormikHelpers } from "formik";
import { countryConfig } from "@utils/countryConfig";
import { formatErrorMessage } from "@utils/formatErrorMessage";
import { useUserProfile } from "@providers/profile";
import { UserAddress } from "@providers/profile/ProfileProvider.model";
import { Input, Select } from "@components/primitives/input";
import { AddressValues, addressValues, StateSchema, FormStatusState } from "./Address.model";
import { BillingState } from "../subscription/Subscription.model";
import { CancelButton, ButtonWrap } from "../Account.styled";

export interface AddressFormProps {
  states: Array<StateSchema>;
  address?: UserAddress;
  dispatchBilling: React.Dispatch<React.SetStateAction<BillingState>>;
  formStatus: FormStatusState;
  dispatchFormStatus: React.Dispatch<React.SetStateAction<FormStatusState>>;
  onToggle: (e: any) => void;
}

const AddressForm = ({
  states,
  address,
  dispatchBilling,
  formStatus,
  dispatchFormStatus,
  onToggle,
}: AddressFormProps) => {
  const countryConfigData = countryConfig().address;

  const {
    changeAddress,
    profileState: { authToken },
  } = useUserProfile();

  const onSubmit = async (
    values: AddressValues,
    { setSubmitting }: FormikHelpers<AddressValues>
  ) => {
    setSubmitting(true);
    let submitState = { ...formStatus, error: null, loading: true };
    dispatchFormStatus({ ...submitState });

    try {
      await changeAddress(authToken, values);

      dispatchBilling((prevState) => ({
        ...prevState,
        address: { ...prevState.address, ...values },
      }));
    } catch (e) {
      submitState = { ...submitState, error: formatErrorMessage(e) };
    } finally {
      setSubmitting(false);
      dispatchFormStatus({ ...submitState, loading: false, editing: false });
    }
  };

  return (
    <Formik
      initialValues={addressValues(address)}
      validationSchema={countryConfigData.billingAddressSchema}
      onSubmit={onSubmit}>
      {(props: FormikProps<AddressValues>) => {
        const { handleChange, handleBlur, errors, touched, values, isSubmitting } = props;

        return (
          <Form noValidate className="grid-x grid-margin-x">
            <div className="cell small-12">
              <Input
                type="text"
                id="line1"
                name="line1"
                label="Address Line 1"
                autoComplete="address-line1"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.line1}
                error={errors.line1 && touched.line1 && errors.line1}
              />
            </div>
            {countryConfigData.address2 && (
              <div className="cell small-12">
                <Input
                  type="text"
                  name="line2"
                  label="Apartment, Suite etc."
                  autoComplete="address-line2"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.line2}
                  error={errors.line2 && touched.line2 && errors.line2}
                />
              </div>
            )}
            <div className="cell small-12 medium-4">
              <Input
                type="text"
                name="city"
                label="City"
                autoComplete="address-level2"
                required
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.city}
                error={errors.city && touched.city && errors.city}
              />
            </div>
            <div className="cell small-12 medium-4">
              <Select
                name="state"
                label={countryConfigData.stateLabel}
                required
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.state}
                error={errors.state && touched.state && errors.state}>
                <option value={""} />
                {states.map((s) => (
                  <option key={s._key} value={s.abbr}>
                    {s.name}
                  </option>
                ))}
              </Select>
            </div>
            <div className="cell small-12 medium-4">
              <Input
                type="text"
                name="postal_code"
                label={countryConfigData.zipLabel}
                autoComplete="postal-code"
                required
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.postal_code}
                error={errors.postal_code && touched.postal_code && errors.postal_code}
              />
            </div>
            <ButtonWrap className="cell small-12">
              <input
                type="submit"
                className="button inverted"
                value="Update"
                disabled={isSubmitting}
              />
              <CancelButton
                onClick={(e: React.MouseEvent<HTMLElement>) => {
                  if (e.target === document.activeElement) {
                    onToggle(e);
                  }
                }}>
                or cancel
              </CancelButton>
            </ButtonWrap>
          </Form>
        );
      }}
    </Formik>
  );
};

export default AddressForm;
